// @ts-nocheck
import React, { useCallback, useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import MUIButton from '@mui/material/Button';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { toast } from 'react-toastify';
import css from '../shared/css';
import Button from '../shared/button';
import PermissionLayers from '../permission-layers';
import { Layer } from '../models/layer';
import { PickableApp, FulcrumLayer } from './fulcrum-layer';
import XYZLayer from './xyz-layer';
import WMSLayer from './wms-layer';
import FeatureServiceLayer from './feature-service-layer';
import JsonLayer from './json-layer';
import MBTilesLayer from './mbtiles-layer';
import KMLLayer from './kml-layer';
import { MapView, convertFulcrumLayerToMapLayer } from '../shared/map';
import ArcGisMapView from '../shared/map/arc-gis-map-view';
import SearchPlugin from '../shared/map/plugins/search';
import { App, useApp } from '../hooks/use-app';
import LayerSettings from '../models/layer-settings';
import DefaultPlugins from '../shared/map/plugins/default';
import Basemap from '../shared/map/plugins/basemap';
import PreviewLayer from '../shared/map/plugins/preview-layer';
import LayerSettingsReferenceHandler from '../shared/map/plugins/utils/layer-settings-reference-handler';

interface Props {
  originalLayer: Layer;
  layerTypes: string[][];
  apps: PickableApp[];
  isNew: boolean;
  roles: Array<any>;
}

const LayerTypeInfoIcon = styled(InfoOutlinedIcon)`
  color: #5856D6;
  position: relative;
  top: calc(50% - 12px);
`;

const LayerTypeAlert = styled(Alert)`
  padding: 16px;
  background: linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), #5856D6;
  margin: 12px 0px;
  width: 446px;
  color: #232256;
`;

const FSLButton = styled(MUIButton)`
  background: linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), #5856D6;
  font-style: normal;
  font-weight: 500;
  font-size: 13px;
  line-height: 22px;
  color: #232256;
  letter-spacing: 0.46px;
  padding: 0;
  text-transform: uppercase;
  transition: none;
  top: 30px;
  &:hover{
    background: rgba(88, 86, 214, 0.04);
    color: #232256;
  }
  width: 100px;
  min-width: auto;
`;

const LayerUploader: React.FC<Props> = ({
  originalLayer,
  layerTypes,
  apps,
  isNew,
  roles,
}: Props) => {
  const app = useApp();
  const [layer, setLayer] = useState(originalLayer);
  const [previewLayer, setPreviewLayer] = useState(originalLayer);
  const [mapViewLayers, setMapViewLayers] = useState(getMapViewLayers(originalLayer, app));
  const [upload, setUpload] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [sending, setSending] = useState(false);
  const [toastId, setToastId] = useState(undefined);

  useEffect(() => {
    const validInputName = layer.name.length > 0 && layer.name.trim().length > 0;
    setDisabled(!validInputName || sending);
  });

  const setLayerSource = useCallback((source: string) => {
    setLayer({ ...layer, source });
  }, [setLayer, layer]);

  const setLayerFile = useCallback((key: string, fileName: string) => {
    const name = fileName.replace(/\.(mbtiles|kml|kmz)$/i, '');
    setLayer({
      ...layer, key, file_name: fileName, name,
    });
  }, [setLayer, layer]);

  const uploadBegin = () => {
    setSending(true);
  };

  const afterUpload = () => {
    const queryString = new URLSearchParams({
      'layer[type]': layer.type,
      'layer[key]': layer.key,
      'layer[name]': layer.name,
      'layer[file_name]': layer.file_name,
      'layer[description]': layer.description ?? '',
    }).toString();

    window.location = `/layers/finalize?${queryString}`;
  };

  const onLayerError = () => {
    setDisabled(false);
    setUpload(false);
    setSending(false);
  };

  const renderLayerImporter = (startUpload: boolean) => {
    const layerType = layer.type || '';
    const fileUploaderProps = {
      startUpload,
      onUploadBegin: uploadBegin,
      onComplete: afterUpload,
      onError: onLayerError,
      maxFiles: 1,
    };
    switch (layerType.toLowerCase()) {
      case 'kml': {
        return (
          <KMLLayer
            isNew={isNew}
            fileUploaderProps={fileUploaderProps}
            setLayerFile={setLayerFile}
          />
        );
      }
      case 'mbtiles': {
        return (
          <MBTilesLayer
            isNew={isNew}
            fileUploaderProps={fileUploaderProps}
            setLayerFile={setLayerFile}
          />
        );
      }
      case 'fulcrum': {
        return (
          <FulcrumLayer
            apps={apps}
            layerSource={layer.source}
            setLayerSource={setLayerSource}
          />
        );
      }
      case 'xyz': {
        return (<XYZLayer layerSource={layer.source} setLayerSource={setLayerSource} />);
      }
      case 'feature-service': {
        return (<FeatureServiceLayer layerSource={layer.source} setLayerSource={setLayerSource} />);
      }
      case 'wms': {
        return (<WMSLayer layerSource={layer.source} setLayerSource={setLayerSource} />);
      }
      case 'tilejson': {
        return (<JsonLayer title="URL or TileJSON" defaultValue={layer.source} />);
      }
      case 'geojson': {
        return (<JsonLayer title="URL or GeoJSON" defaultValue={layer.source} httpsOnly />);
      }
      default: {
        return null;
      }
    }
  };

  layer.name = layer.name || '';

  const saveLayer = (e) => {
    setDisabled(true);
    setSending(true);
    if (isNew && (layer.type === 'mbtiles' || layer.type === 'kml')) {
      e.preventDefault();
      setUpload(true);
    } else {
      document.getElementsByClassName('form-stacked')[0].submit();
    }
  };

  const handleUpdateLayerType = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLayer({
      ...layer,
      type: e.target.value,
      source: null,
      file_size: 0,
    });
    setMapViewLayers([]);
  };

  const updatePreview = () => {
    const newMapViewLayers = getMapViewLayers(layer, app);
    if (newMapViewLayers.length || layer?.type === 'feature-service') {
      setPreviewLayer({ ...layer, id: `${previewLayer.id}-` });
      setMapViewLayers({ ...newMapViewLayers });
    }
  };

  const renderLayerTypeSelect = () => (
    <select
      data-testid="new-layer-type-select"
      id="layer_layer_type"
      name="layer[type]"
      required
      value={layer.type || ''}
      onChange={handleUpdateLayerType}
    >
      <option value="">Select Layer Type</option>
      {
        layerTypes
          .filter((layerType) => layerType[0] !== 'all')
          .filter((layerType) => layerType[0] !== 'kml' || app.globals.ldflags['kml-layers'])
          .map((layerType) => (
            <option key={layerType[0]} value={layerType[0]}>{layerType[1]}</option>
          ))
      }
    </select>
  );

  const renderLayerType = () => (
    <div data-testid="uploaded-layer-type" className="layer-type input">
      {layerTypes.filter((x) => x[0] === layer.type)[0][1]}
    </div>
  );

  const renderLoadingState = () => {
    if (sending && !toastId) {
      const message = isNew ? 'Processing new map layer' : 'Updating map layer';
      setToastId(toast.loading(message));
    }

    return <></>;
  };

  const renderFeatureServiceLayerAlert = () => (
    <LayerTypeAlert
      data-testid="fsl-alert"
      icon={<LayerTypeInfoIcon />}
      action={(
        <FSLButton
          size="small"
          data-testid="fsl-alert-button"
          target="_blank"
          rel="noopener noreferrer"
          href="https://help.fulcrumapp.com/en/articles/7910802-how-do-i-add-a-public-feature-service-layer"
        >
          Learn More
        </FSLButton>
      )}
    >
      Feature Service layers that are not
      publicly accessible should be managed
      in ArcGIS and accessed by
      authenticating within Fulcrum.
    </LayerTypeAlert>
  );
  const renderKMLLayerAlert = () => (
    <LayerTypeAlert
      data-testid="kml-alert"
      icon={<LayerTypeInfoIcon />}
    >
      KML layers exceeding 10MB will not display on the web map. KML layers are only supported on the Esri Map engine.
    </LayerTypeAlert>
  );

  const esriEnabled = app.globals.flags.web_map_engine === 'ESRI';
  const featureServiceLayersEnabled = esriEnabled && layer.type === 'feature-service';
  const showMapPreview = (): boolean => (
    layer.type
      && (layer.type === 'wms' || layer.type === 'xyz' || layer.type === 'fulcrum' || (featureServiceLayersEnabled && esriEnabled))
  );
  const legacyView = <MapView layers={mapViewLayers} />;
  const previewLayerSettings = new LayerSettings(
    [previewLayer],
    previewLayer.source,
    app.globals.config.tile_url,
    app.globals.config.layer_url ?? app.globals.config.tile_url,
    app.preferences,
  );
  const layerToPreview = previewLayerSettings.layerList[0];
  const layerSettingsHandler = LayerSettingsReferenceHandler(previewLayerSettings);
  const plugins = [
    DefaultPlugins(),
    SearchPlugin(),
    Basemap(layerSettingsHandler, app.globals.config.google_maps.api_key),
    PreviewLayer(layerToPreview, app.api.token),
  ];

  const arcGISView = () => <ArcGisMapView plugins={plugins} />;

  return (
    <div className={styles.layerUploader}>
      <div className={styles.layer}>
        <div className={styles.layerForm}>
          <label htmlFor="layer_layer_type" className="required">Layer Type</label>
          <div className="layer-type input">
            {isNew ? renderLayerTypeSelect() : renderLayerType()}
          </div>
          {featureServiceLayersEnabled ? renderFeatureServiceLayerAlert() : null}
          {layer.type === 'kml' ? renderKMLLayerAlert() : null}
          <label htmlFor="layer_name" className="required">Name</label>
          <div className="layer-name input">
            <input
              type="text"
              name="layer[name]"
              id="layer_name"
              onChange={(e) => setLayer({ ...layer, name: e.target.value })}
              value={layer.name || ''}
              className="span6"
              required
            />
          </div>
          <label htmlFor="layer_description">Description</label>
          <div className="layer-description input">
            <textarea
              name="layer[description]"
              id="layer_description"
              onChange={(e) => setLayer({ ...layer, description: e.target.value })}
              className="span6"
              value={layer.description || ''}
            />
          </div>
          <div>
            {renderLayerImporter(upload)}
          </div>
          <hr />
          <PermissionLayers roles={roles} layer_id={!isNew ? layer.id : null} />
        </div>
        {
          showMapPreview()
            && (
              <div className={styles.layerPreview}>
                <label>Layer Preview</label>
                {esriEnabled ? arcGISView() : legacyView}
              </div>
            )
        }
      </div>
      <div className={styles.buttonContainer}>
        {
          showMapPreview()
            && (
              <Button quiet onClick={updatePreview}>
                Update Preview
              </Button>
            )
        }
        <Button quiet link href="/layers">
          Cancel
        </Button>
        <Button affirm onClick={saveLayer} disabled={disabled} title={isNew ? 'Create Layer' : 'Update Layer'}>
          {isNew ? 'Create Layer' : 'Update Layer'}
        </Button>
      </div>

      { renderLoadingState() }
    </div>
  );
};

function getMapViewLayers(layer: Layer, app: App) {
  if (!layer?.type) {
    return [];
  }

  try {
    return [convertFulcrumLayerToMapLayer(layer, app)];
  } catch (error) {
    return [];
  }
}

const styles = css({
  layerUploader: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '100%',
    flex: 1,
  },
  layer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
  layerForm: {
  },
  layerPreview: {
    width: '100%',
    height: 525,
    display: 'flex',
    flexDirection: 'column',
    marginLeft: 20,
  },
  buttonContainer: {
    marginTop: 20,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
});

export default LayerUploader;
